import { RECAP_PERMISSION_RELATIONS } from 'features/recaps/constants'
import { getGroup } from 'features/user_groups'
import { fetchOrganizationMembershipsByUserUuids } from 'shared/api/organization_memberships'

const RECAP_PERMISSION_SUBJECT_TYPES = Object.freeze({
  USER: 'user',
  USER_GROUP: 'user_group',
  ORGANIZATION: 'organization',
})

const getTupleSubjectType = (tuple) => tuple.subjectType ?? tuple.subject_type

const getTupleSubjectId = (tuple) => tuple.subjectId ?? tuple.subject_id

export const splitRecapPermissionTuples = (tuples) => {
  if (!Array.isArray(tuples)) {
    return { users: [], userGroups: [], organizations: [] }
  }

  return tuples.reduce((acc, tuple) => {
    const subjectType = getTupleSubjectType(tuple)
    const subjectId = getTupleSubjectId(tuple)

    if (!subjectId) {
      return acc
    }

    const entry = {
      uuid: subjectId,
      relation: tuple.relation,
    }

    if (subjectType === RECAP_PERMISSION_SUBJECT_TYPES.USER) {
      acc.users.push(entry)
    } else if (subjectType === RECAP_PERMISSION_SUBJECT_TYPES.USER_GROUP) {
      acc.userGroups.push(entry)
    } else if (subjectType === RECAP_PERMISSION_SUBJECT_TYPES.ORGANIZATION) {
      acc.organizations.push(entry)
    }

    return acc
  }, { users: [], userGroups: [], organizations: [] })
}

export const countNonOwnerUserGrants = (users, ownerUserUuid) => (
  users.filter(({ uuid }) => uuid !== ownerUserUuid).length
)

export const countShareRecipients = (users, userGroups, ownerUserUuid) => (
  countNonOwnerUserGrants(users, ownerUserUuid)
  + userGroups.reduce((total, { memberCount = 0 }) => total + memberCount, 0)
)

const getGroupSubjectId = (userGroup) => userGroup.uuid ?? String(userGroup.id)

export const suggestionToPendingRecipient = (suggestion) => {
  if (suggestion.type === 'organization_membership') {
    const { entity } = suggestion
    const userUuid = entity.user_uuid

    if (!userUuid) {
      return null
    }

    return {
      id: suggestion.id,
      subjectType: 'user',
      subjectId: userUuid,
      relation: RECAP_PERMISSION_RELATIONS.VIEWER,
      display: {
        name: entity.name ?? '',
        email: entity.email ?? '',
        avatarUrl: entity.avatar_url ?? null,
        avatarLetter: entity.avatar_letter ?? null,
      },
    }
  }

  if (suggestion.type === 'user_group') {
    const { entity } = suggestion
    const groupUuid = getGroupSubjectId(entity)

    if (!groupUuid) {
      return null
    }

    return {
      id: suggestion.id,
      subjectType: 'user_group',
      subjectId: groupUuid,
      relation: RECAP_PERMISSION_RELATIONS.VIEWER,
      display: {
        name: entity.name ?? '',
        memberCount: entity.user_group_relationships_count ?? 0,
      },
    }
  }

  return null
}

export const buildNewRecipientsPayload = (pendingRecipients) => ({
  users: pendingRecipients
    .filter(({ subjectType }) => subjectType === 'user')
    .map(({ subjectId, relation }) => ({ uuid: subjectId, relation })),
  userGroups: pendingRecipients
    .filter(({ subjectType }) => subjectType === 'user_group')
    .map(({ subjectId, relation }) => ({ uuid: subjectId, relation })),
})

const findServerRelation = (entries, uuid) => (
  entries.find((entry) => entry.uuid === uuid)?.relation ?? RECAP_PERMISSION_RELATIONS.VIEWER
)

// Split pending edits into a POST payload (new recipients + relation grants/changes) and a
// DELETE payload (removals). Removals send the original server relation for the tuple.
export const buildPermissionSubmissionPayloads = ({
  pendingPermissionUpdates,
  pendingRecipients,
  accessList,
}) => {
  const newRecipients = buildNewRecipientsPayload(pendingRecipients)
  const postPayload = {
    users: [...newRecipients.users],
    userGroups: [...newRecipients.userGroups],
    organizations: [],
  }
  const deletePayload = { users: [], userGroups: [], organizations: [] }

  Object.entries(pendingPermissionUpdates.users ?? {}).forEach(([uuid, relation]) => {
    if (relation === null) {
      deletePayload.users.push({ uuid, relation: findServerRelation(accessList.users, uuid) })
    } else {
      postPayload.users.push({ uuid, relation })
    }
  })

  Object.entries(pendingPermissionUpdates.userGroups ?? {}).forEach(([uuid, relation]) => {
    if (relation === null) {
      deletePayload.userGroups.push({ uuid, relation: findServerRelation(accessList.userGroups, uuid) })
    } else {
      postPayload.userGroups.push({ uuid, relation })
    }
  })

  const orgRelation = pendingPermissionUpdates.organization
  const organizationUuid = accessList.organization?.uuid

  if (orgRelation !== undefined && organizationUuid != null) {
    const orgUuid = String(organizationUuid)

    if (orgRelation === null) {
      deletePayload.organizations.push({
        uuid: orgUuid,
        relation: accessList.organization?.relation ?? RECAP_PERMISSION_RELATIONS.VIEWER,
      })
    } else {
      postPayload.organizations.push({ uuid: orgUuid, relation: orgRelation })
    }
  }

  const postUserUuids = new Set(postPayload.users.map(({ uuid }) => uuid))
  const postGroupUuids = new Set(postPayload.userGroups.map(({ uuid }) => uuid))
  const postOrgUuids = new Set(postPayload.organizations.map(({ uuid }) => uuid))

  deletePayload.users = deletePayload.users.filter(({ uuid }) => !postUserUuids.has(uuid))
  deletePayload.userGroups = deletePayload.userGroups.filter(({ uuid }) => !postGroupUuids.has(uuid))
  deletePayload.organizations = deletePayload.organizations.filter(({ uuid }) => !postOrgUuids.has(uuid))

  return { postPayload, deletePayload }
}

export const isPermissionPayloadEmpty = (payload) => (
  payload.users.length === 0
  && payload.userGroups.length === 0
  && payload.organizations.length === 0
)

const buildUserRowFromPendingRecipient = (recipient, relation) => ({
  uuid: recipient.subjectId,
  relation,
  name: recipient.display.name ?? '',
  email: recipient.display.email ?? '',
  avatarUrl: recipient.display.avatarUrl ?? null,
  avatarLetter: recipient.display.avatarLetter ?? null,
})

const buildGroupRowFromPendingRecipient = (recipient, relation) => ({
  uuid: recipient.subjectId,
  relation,
  name: recipient.display.name ?? '',
  memberCount: recipient.display.memberCount ?? 0,
})

const upsertAccessListUser = (users, { uuid, relation }, recipientsAtSubmit) => {
  const existingIndex = users.findIndex((user) => user.uuid === uuid)

  if (existingIndex >= 0) {
    const nextUsers = [...users]
    nextUsers[existingIndex] = { ...nextUsers[existingIndex], relation }
    return nextUsers
  }

  const recipientAtSubmit = recipientsAtSubmit.find(
    (recipient) => recipient.subjectType === 'user' && recipient.subjectId === uuid,
  )

  if (recipientAtSubmit) {
    return [...users, buildUserRowFromPendingRecipient(recipientAtSubmit, relation)]
  }

  return [...users, {
    uuid,
    relation,
    name: '',
    email: '',
    avatarUrl: null,
    avatarLetter: null,
  }]
}

const upsertAccessListUserGroup = (userGroups, { uuid, relation }, recipientsAtSubmit) => {
  const existingIndex = userGroups.findIndex((userGroup) => userGroup.uuid === uuid)

  if (existingIndex >= 0) {
    const nextUserGroups = [...userGroups]
    nextUserGroups[existingIndex] = { ...nextUserGroups[existingIndex], relation }
    return nextUserGroups
  }

  const recipientAtSubmit = recipientsAtSubmit.find(
    (recipient) => recipient.subjectType === 'user_group' && recipient.subjectId === uuid,
  )

  if (recipientAtSubmit) {
    return [...userGroups, buildGroupRowFromPendingRecipient(recipientAtSubmit, relation)]
  }

  return [...userGroups, {
    uuid,
    relation,
    name: '',
    memberCount: 0,
  }]
}

export const mergePostPermissionsIntoAccessList = (accessList, postResponse, recipientsAtSubmit = []) => {
  let { users, userGroups, organization } = accessList;

  (postResponse?.users ?? []).forEach((entry) => {
    users = upsertAccessListUser(users, entry, recipientsAtSubmit)
  });

  (postResponse?.userGroups ?? []).forEach((entry) => {
    userGroups = upsertAccessListUserGroup(userGroups, entry, recipientsAtSubmit)
  });

  (postResponse?.organizations ?? []).forEach(({ uuid, relation }) => {
    if (organization && String(organization.uuid) === String(uuid)) {
      organization = { ...organization, relation }
    }
  })

  return {
    ...accessList,
    users,
    userGroups,
    organization,
  }
}

export const mergeDeletePermissionsIntoAccessList = (accessList, deletePayload) => {
  const removedUserUuids = new Set((deletePayload?.users ?? []).map(({ uuid }) => uuid))
  const removedGroupUuids = new Set((deletePayload?.userGroups ?? []).map(({ uuid }) => uuid))
  const removedOrgUuids = new Set((deletePayload?.organizations ?? []).map(({ uuid }) => String(uuid)))

  let { users, userGroups, organization } = accessList

  users = users.filter((user) => !removedUserUuids.has(user.uuid))
  userGroups = userGroups.filter((userGroup) => !removedGroupUuids.has(userGroup.uuid))

  if (organization && removedOrgUuids.has(String(organization.uuid))) {
    organization = { ...organization, relation: null }
  }

  return {
    ...accessList,
    users,
    userGroups,
    organization,
  }
}

const countPendingRecipients = (pendingRecipients) => (
  pendingRecipients.reduce((total, recipient) => {
    if (recipient.subjectType === 'user_group') {
      return total + (recipient.display.memberCount ?? 0)
    }

    return total + 1
  }, 0)
)

export const countEffectiveRecipients = ({
  users,
  userGroups,
  ownerUserUuid,
  pendingRecipients,
}) => (
  countShareRecipients(users, userGroups, ownerUserUuid)
  + countPendingRecipients(pendingRecipients)
)

export const buildSuggestionExclusion = ({
  pendingRecipients,
  accessListUsers,
  accessListUserGroups,
  getUserRelation,
  getUserGroupRelation,
  ownerUserUuid,
}) => {
  const excludedChipIds = new Set(pendingRecipients.map(({ id }) => id))
  const excludedUserUuids = new Set(
    pendingRecipients
      .filter(({ subjectType }) => subjectType === 'user')
      .map(({ subjectId }) => subjectId),
  )
  const excludedGroupUuids = new Set(
    pendingRecipients
      .filter(({ subjectType }) => subjectType === 'user_group')
      .map(({ subjectId }) => subjectId),
  )

  if (ownerUserUuid) {
    excludedUserUuids.add(ownerUserUuid)
  }

  accessListUsers.forEach((user) => {
    if (getUserRelation(user.uuid, user.relation) !== null) {
      excludedUserUuids.add(user.uuid)
    }
  })

  accessListUserGroups.forEach((userGroup) => {
    if (getUserGroupRelation(userGroup.uuid, userGroup.relation) !== null) {
      excludedGroupUuids.add(userGroup.uuid)
    }
  })

  return { excludedChipIds, excludedUserUuids, excludedGroupUuids }
}

const isSuggestionExcluded = (suggestion, exclusion) => {
  if (exclusion.excludedChipIds.has(suggestion.id)) {
    return true
  }

  if (suggestion.type === 'organization_membership') {
    const userUuid = suggestion.entity.user_uuid

    return Boolean(userUuid && exclusion.excludedUserUuids.has(userUuid))
  }

  if (suggestion.type === 'user_group') {
    const groupUuid = getGroupSubjectId(suggestion.entity)

    return Boolean(groupUuid && exclusion.excludedGroupUuids.has(groupUuid))
  }

  return false
}

export const filterExcludedSuggestions = (suggestions, exclusion) => (
  suggestions.filter((suggestion) => !isSuggestionExcluded(suggestion, exclusion))
)

// Display the user/group's pending relation if it exists
export const relationToDisplay = (pendingMap, uuid, serverRelation) => (
  Object.hasOwn(pendingMap, uuid) ? pendingMap[uuid] : serverRelation
)

// Display the organization's pending relation if it exists
export const organizationRelationToDisplay = (pendingRelation, serverRelation) => (
  pendingRelation !== undefined ? pendingRelation : serverRelation
)

export const buildAccessListOrganization = (orgUuid, organizations) => {
  if (!orgUuid) {
    return null
  }

  const currentOrganizationGrant = organizations.find(({ uuid }) => String(uuid) === String(orgUuid)) ?? null

  return currentOrganizationGrant ? {
    uuid: currentOrganizationGrant.uuid,
    relation: currentOrganizationGrant.relation
  } : { uuid: orgUuid, relation: null }
}

const mapUserOrgMembershipToAccessListUser = (userOrgMembership, { uuid, relation, isCurrentUser = false }) => ({
  uuid,
  relation,
  name: userOrgMembership?.name ?? uuid,
  email: userOrgMembership?.email ?? '',
  avatarUrl: userOrgMembership?.avatar_url ?? null,
  // Use first letter of name or email, since avatar_letter is not available in the organization membership
  avatarLetter: userOrgMembership?.name?.[0]?.toUpperCase() ?? userOrgMembership?.email?.[0]?.toUpperCase() ?? null,
  isCurrentUser,
})

const loadMembershipsByUserUuid = async (userUuids) => {
  if (!userUuids.length) {
    return {}
  }

  try {
    const memberships = await fetchOrganizationMembershipsByUserUuids(userUuids)

    return memberships.reduce((acc, membership) => {
      if (membership.user_uuid) {
        acc[membership.user_uuid] = membership
      }

      return acc
    }, {})
  } catch {
    return {}
  }
}

// currentUser object is from the current user's profile, not the organization membership
const resolveAccessListOwner = ({ owner, currentUser }) => {
  const ownerUserUuid = owner?.uuid

  if (!ownerUserUuid) {
    return null
  }

  if (currentUser?.uuid === ownerUserUuid) {
    return {
      uuid: ownerUserUuid,
      relation: RECAP_PERMISSION_RELATIONS.OWNER,
      name: currentUser.profile?.name ?? currentUser.name,
      email: currentUser.email,
      avatarUrl: currentUser.avatar_url,
      avatarLetter: currentUser.avatar_letter,
      isCurrentUser: true,
    }
  }

  if (owner.displayName || owner.name || owner.email) {
    const name = owner.name ?? owner.displayName ?? null

    return {
      uuid: ownerUserUuid,
      relation: RECAP_PERMISSION_RELATIONS.OWNER,
      name: name ?? ownerUserUuid,
      email: owner.email ?? '',
      avatarUrl: owner.avatarUrl ?? null,
      avatarLetter: owner.avatarLetter ?? null,
      isCurrentUser: false,
    }
  }

  return mapUserOrgMembershipToAccessListUser(null, {
    uuid: ownerUserUuid,
    relation: RECAP_PERMISSION_RELATIONS.OWNER,
    isCurrentUser: false,
  })
}

export const enrichAccessListUsers = async (_dispatch, users, owner, currentUser) => {
  const ownerUserUuid = owner?.uuid
  const nonOwnerUsers = users.filter(({ uuid }) => uuid !== ownerUserUuid)
  const membershipsByUserUuid = await loadMembershipsByUserUuid(
    nonOwnerUsers.map(({ uuid }) => uuid),
  )

  return {
    owner: resolveAccessListOwner({ owner, currentUser }),
    users: nonOwnerUsers.map(({ uuid, relation }) => (
      mapUserOrgMembershipToAccessListUser(membershipsByUserUuid[uuid] ?? null, { uuid, relation })
    )),
  }
}

export const enrichAccessListUserGroups = async (userGroups) => {
  const enrichedGroups = await Promise.all(
    userGroups.map(async ({ uuid, relation }) => {
      const [success, response] = await getGroup(uuid)
      if (!success) {
        return {
          uuid,
          relation,
          name: '',
          memberCount: 0,
          forbidden: true,
        }
      }

      const memberCount = response?.user_group_relationships_count ?? 0
      return {
        uuid,
        relation,
        name: response?.name ?? '',
        memberCount,
        forbidden: false,
      }
    }),
  )

  return enrichedGroups
}
